/**
 * « Soyez prévenu dès l'ouverture des inscriptions. »
 *
 * ## Pourquoi cette carte existe
 *
 * 1 997 comptes sur 2 386 ne sont pas abonnés — 16 % le sont. La case existe
 * pourtant depuis toujours : elle est enterrée dans le profil, sous un bouton
 * « Enregistrer » qu'il faut penser à cliquer. Personne ne la trouve.
 *
 * ## Pourquoi pas une fenêtre qui s'ouvre
 *
 * Une modale à l'arrivée se ferme au réflexe, avant même d'être lue — et elle
 * entrerait en concurrence avec le bloc « à faire », qui est la raison d'être
 * de cette page. Une carte en bas de page attend son tour.
 *
 * ## Pourquoi seulement quand il n'y a rien à faire
 *
 * Un parent qui a trois fiches à compléter n'a pas besoin d'une quatrième
 * demande. Celui dont tout est à jour a de la place, et il vient de faire
 * quelque chose : c'est le moment où l'on dit oui.
 *
 * ## Un clic, aucun champ
 *
 * On connaît son adresse, il est connecté. C'est tout l'écart avec le
 * formulaire public — demander de retaper une adresse qu'on a déjà, c'est
 * demander un effort pour rien.
 *
 * ## Le refus est retenu sur le COMPTE
 *
 * `newsletter_proposee_le` vit en base, pas dans le navigateur : sinon le
 * parent qui refuse sur son téléphone reverrait la carte sur son ordinateur, et
 * la troisième fois ce n'est plus une invitation.
 */

import { useState } from 'react';
import { Bell, Check, Loader2, X } from 'lucide-react';

const InvitationNewsletter = ({
  onAccepter,
  onRefuser,
}: {
  onAccepter: () => Promise<void>;
  onRefuser: () => Promise<void>;
}) => {
  const [enCours, setEnCours] = useState<'oui' | 'non' | null>(null);
  const [accepte, setAccepte] = useState(false);

  const repondre = async (reponse: 'oui' | 'non') => {
    if (enCours) return;
    setEnCours(reponse);
    try {
      if (reponse === 'oui') {
        await onAccepter();
        setAccepte(true);
      } else {
        await onRefuser();
      }
    } finally {
      setEnCours(null);
    }
  };

  if (accepte) {
    return (
      <section className="mt-8 flex items-center gap-3 rounded-2xl bg-green-50 p-4 ring-1 ring-green-200">
        <Check className="h-5 w-5 flex-shrink-0 text-green-600" aria-hidden="true" />
        <p className="text-sm text-green-900">
          C’est noté. Vous serez prévenu dès l’ouverture des prochaines inscriptions.
        </p>
      </section>
    );
  }

  return (
    <section className="mt-8 rounded-2xl bg-primary-50 p-5 ring-1 ring-primary-100">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-white">
          <Bell className="h-5 w-5 text-primary-600" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold text-gray-900">
            Soyez prévenu à l’ouverture des inscriptions
          </h2>
          {/*
            La frequence est dite : c'est la question que se pose vraiment
            quelqu'un a qui on demande son adresse, et la taire fait refuser.
          */}
          <p className="mt-1 text-sm text-gray-700">
            Nos semaines les plus demandées partent en quelques jours. Nous vous écrivons quand
            elles ouvrent — quelques courriels par an, rien d’autre.
          </p>

          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
            <button
              type="button"
              onClick={() => repondre('oui')}
              disabled={enCours !== null}
              className="btn-primary inline-flex min-h-[48px] items-center justify-center disabled:opacity-60"
            >
              {enCours === 'oui' ? (
                <Loader2 className="mr-2 h-5 w-5 animate-spin" aria-hidden="true" />
              ) : (
                <Bell className="mr-2 h-5 w-5" aria-hidden="true" />
              )}
              Prévenez-moi
            </button>
            <button
              type="button"
              onClick={() => repondre('non')}
              disabled={enCours !== null}
              className="inline-flex min-h-[48px] items-center justify-center px-3 text-sm font-medium text-gray-600 hover:text-gray-900 disabled:opacity-60"
            >
              {enCours === 'non' ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <X className="mr-2 h-4 w-4" aria-hidden="true" />
              )}
              Non merci
            </button>
          </div>

          <p className="mt-2 text-xs text-gray-500">
            Vous pouvez vous désabonner en un clic, depuis chaque courriel ou depuis votre profil.
          </p>
        </div>
      </div>
    </section>
  );
};

export default InvitationNewsletter;
